import { parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, redirect, useSearchParams } from 'react-router'
import { z } from 'zod'

import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { MAGIC_LINK_SUPPORT_MESSAGE } from '#app/domains/auth/login.constants.ts'
import { requestLoginMagicLink } from '#app/domains/auth/login.server.ts'
import { type Route } from '#app/routes/_auth+/+types/login.ts'
import { useLoginFormController } from '#app/routes/_auth+/login.controller.tsx'
import { MagicLinkSchema } from '#app/routes/_auth+/login.schema.ts'
import { LoginFormView } from '#app/routes/_auth+/login.view.tsx'
import { getUserId, requireAnonymous } from '#app/utils/auth.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getErrorMessage } from '#app/utils/misc.tsx'
import { createToastHeaders } from '#app/utils/toast.server.ts'

export const handle: SEOHandle = { getSitemapEntries: () => null }

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	if (userId) {
		return redirect('/d')
	}
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			MagicLinkSchema.transform(async (data, ctx) => {
				if (intent !== null) return data
				try {
					const result = await requestLoginMagicLink({
						request,
						email: data.email,
						redirectTo: data.redirectTo,
					})

					if (result.status === 'error') {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							...(result.field ? { path: [result.field] } : {}),
							message: result.message,
						})
						return z.NEVER
					}

					return {
						...data,
						email: result.email,
						devVerifyUrl: result.devVerifyUrl ?? undefined,
					}
				} catch (e) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: (() => {
							const parsed = getErrorMessage(e)
							if (!parsed || parsed === 'Unknown error')
								return MAGIC_LINK_SUPPORT_MESSAGE
							return parsed
						})(),
					})
					return z.NEVER
				}
			}),
		async: true,
	})

	const result = submission.reply()
	let headers: Headers | undefined
	if (submission.status === 'success') {
		headers = await createToastHeaders({
			type: 'success',
			description: 'Check your email for a sign-in link.',
		})
	} else if (submission.status === 'error') {
		headers = await createToastHeaders({
			type: 'error',
			description: 'Failed to send sign-in link',
		})
	}
	return data(
		{ result },
		{
			status: submission.status === 'error' ? 400 : 200,
			headers,
		},
	)
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo') ?? '/d'
	const view = useLoginFormController({ actionData, redirectTo })
	return <LoginFormView view={view} />
}

export const meta: Route.MetaFunction = () => [{ title: 'Login' }]

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
